import { FC, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { CashAccount } from '@shared/api/cashAccount/cashAccount.interface';
import { Button } from '@shared/components/Button';
import ResizableBox from '@shared/components/ResizableBox';
import { Icon } from '@shared/components/icons';
import { notificateSuccess } from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { unsecuredCopyToClipboard } from '@shared/utils/clipboard.util';
import { getBankLogo } from '@shared/utils/getBankLogo';

const DURATION = 0.3;

interface Props {
  cashAccount?: CashAccount;
  additionalCashAccountList?: CashAccount[];
  forMember?: boolean;
  cashAccountEditOpenId?: string;
  emptyAccountMessage?: string;
  accountExpose?: boolean;
  //* 조의금 사절(지도사 계좌 설정·관리자 커스텀 설정): 계좌 섹션 전체를 안내 문구로 대체
  hideCondolenceMoney?: boolean;
  condolenceMoneyMessage?: string;
}

export const DEFAULT_CONDOLENCE_MONEY_MESSAGE =
  '고인의 뜻에 따라 조의금은 정중히 사양합니다.\n따뜻한 마음만 감사히 받겠습니다.';

export const MemberMoney: FC<Props> = function MemberMoney({
  cashAccount,
  forMember,
  cashAccountEditOpenId,
  additionalCashAccountList,
  emptyAccountMessage,
  accountExpose,
  hideCondolenceMoney,
  condolenceMoneyMessage,
}: Props) {
  const { ChevronDown, ChevronUp, MoneyBag } = Icon();
  const [fold, setFold] = useState(!accountExpose);
  if (hideCondolenceMoney) {
    return (
      <CondolenceMoneyNotice
        message={condolenceMoneyMessage?.trim() || DEFAULT_CONDOLENCE_MONEY_MESSAGE}
        forMember={forMember}
      />
    );
  }
  return (
    <MemberMoneyAccounts
      cashAccount={cashAccount}
      forMember={forMember}
      cashAccountEditOpenId={cashAccountEditOpenId}
      additionalCashAccountList={additionalCashAccountList}
      emptyAccountMessage={emptyAccountMessage}
      fold={fold}
      setFold={setFold}
      ChevronDown={ChevronDown}
      ChevronUp={ChevronUp}
      MoneyBag={MoneyBag}
    />
  );
};

type IconComponent = ReturnType<typeof Icon>['MoneyBag'];

//* 조의금 사절 안내 — "마음 전하실 곳(부의금)" 제목·아이콘은 문구와 어긋나므로 빼고, 구분선 사이 문구만 조용히 둔다
const CondolenceMoneyNotice: FC<{
  message: string;
  forMember?: boolean;
}> = ({ message, forMember }) => (
  <div className="bg-white px-2">
    <div className="px-4 pb-3 pt-2">
      <div className="mx-auto max-w-[280px] py-3 text-center">
        {/* 주제 라벨 — 관리자가 문구를 바꿔도 무엇에 대한 안내인지는 남긴다(섹션 제목·아이콘은 쓰지 않음) */}
        <p className="mb-2 text-xs font-semibold tracking-wide text-gray-400">
          조의금 안내
        </p>
        <div className="mb-3 flex justify-center">
          <span className="inline-block h-px w-10 theme-bg-main" />
        </div>
        <p className="whitespace-pre-line break-keep text-[15px] font-semibold leading-relaxed tracking-wide text-gray-700">
          {message}
        </p>
        <div className="mt-3 flex justify-center">
          <span className="inline-block h-px w-10 theme-bg-main" />
        </div>
      </div>
      {forMember && (
        <p className="pb-1 text-center text-xs text-gray-400">
          조의금 사절 설정으로 부의금 계좌 안내가 표시되지 않습니다.
        </p>
      )}
    </div>
  </div>
);

//* 조문객 화면과 같은 컴포넌트로 미리보기 (문구 길이·줄바꿈을 바로 확인)
export const CondolenceMoneyPreview: FC<{ message: string }> = ({ message }) => (
  <div className="mt-2">
    <p className="mb-1 text-[11px] text-gray-400">부고장 미리보기</p>
    <div className="pointer-events-none mx-auto max-w-[360px] rounded-lg border border-dashed border-gray-300 py-2">
      <MemberMoney hideCondolenceMoney condolenceMoneyMessage={message} />
    </div>
  </div>
);

const MemberMoneyAccounts: FC<
  Omit<Props, 'accountExpose' | 'hideCondolenceMoney' | 'condolenceMoneyMessage'> & {
    fold: boolean;
    setFold: (v: boolean) => void;
    ChevronDown: IconComponent;
    ChevronUp: IconComponent;
    MoneyBag: IconComponent;
  }
> = function MemberMoneyAccounts({
  cashAccount,
  forMember,
  cashAccountEditOpenId,
  additionalCashAccountList,
  emptyAccountMessage,
  fold,
  setFold,
  ChevronDown,
  ChevronUp,
  MoneyBag,
}) {
  const setCashAccountEditOpen = useSetRecoilState(
    simpleOpenAtomFamily(cashAccountEditOpenId),
  );
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);

  const verifiedAccountList = useMemo(
    () =>
      [cashAccount, ...(additionalCashAccountList ?? [])].filter(
        (account): account is CashAccount => !!account?.isVerified,
      ),
    [cashAccount, additionalCashAccountList],
  );

  const renderCashAccount = (account: CashAccount, index: number) => {
    const bankLogo = getBankLogo(account.bank);
    const copyButton = (
      <p
        className="absolute right-0 top-0 cursor-pointer rounded-md border bg-white px-2 py-0.5 text-[14px] font-bold theme-text-main theme-border-main"
        onClick={async (): Promise<void> => {
          navigator.clipboard
            .writeText(`${account.bank} ${account.account}`)
            .then(() => {
              notificateSuccess(notifiacationInstance, '계좌복사 완료');
            })
            .catch(() => {
              unsecuredCopyToClipboard(
                `${account.bank} ${account.account}`,
                '계좌복사 완료',
                notifiacationInstance,
              );
            });
        }}
      >
        계좌복사
      </p>
    );
    return (
      <div
        key={account._id}
        className={`flex flex-col gap-2 text-15 font-medium ${
          index > 0 ? 'mt-3 border-t pt-3' : ''
        }`}
      >
        {bankLogo ? (
          //* 은행명 | 예금주 / 로고 | 계좌번호 — 왼쪽 칸 폭을 맞춰 예금주와 계좌번호의 시작점을 정렬
          <div className="relative grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2">
            <p className="break-keep">{account.bank}</p>
            <p className="break-keep">{account.name}</p>
            <img src={bankLogo} alt="" className="h-6 w-6" />
            <p className="break-keep">{account.account}</p>
            {copyButton}
          </div>
        ) : (
          <>
            <div className="relative flex justify-between gap-1">
              <p>{account.name}</p>
              {copyButton}
            </div>
            <div className="flex gap-5">
              <p className="break-keep">{account.bank}</p>
              <p className="break-keep">{account.account}</p>
            </div>
          </>
        )}
      </div>
    );
  };

  // 본인 계좌가 없어도 추가 계좌가 하나라도 있으면 빈 안내는 띄우지 않는다.
  const emptyContent = useMemo(() => {
    const message =
      emptyAccountMessage ||
      (forMember
        ? '아직 등록된 부의금 계좌가 없습니다.\n아래 버튼으로 등록해 주세요.'
        : '아직 안내된 부의금 계좌가 없습니다.');
    return (
      <div className="mx-auto max-w-[280px] py-3 text-center">
        <div className="mb-2 flex justify-center">
          <span className="inline-block h-px w-10 bg-gray-300" />
        </div>
        <p className="whitespace-pre-line break-keep text-[15px] font-semibold leading-relaxed tracking-wide text-gray-700">
          {message}
        </p>
        <div className="mt-2 flex justify-center">
          <span className="inline-block h-px w-10 bg-gray-300" />
        </div>
      </div>
    );
  }, [emptyAccountMessage, forMember]);

  const memberCashAccountContent = useMemo(() => {
    if (forMember) {
      return (
        <div className="border-t pt-3">
          <div className="center-box">
            <Button
              className="button-no-border p-2 theme-text-3 theme-bg-main"
              onClick={() => {
                setCashAccountEditOpen(true);
              }}
            >
              {verifiedAccountList.length > 0 ? '부의금정보수정' : '부의금 계좌 등록'}
            </Button>
          </div>
        </div>
      );
    }
  }, [forMember, setCashAccountEditOpen, verifiedAccountList.length]);

  return (
    <div className="bg-white px-2">
      <div
        className={`flex items-center justify-between py-2 ${
          fold ? 'cursor-pointer' : ''
        }`}
        onClick={() => {
          if (fold) {
            setFold(!fold);
          }
        }}
      >
        <div className="center-box gap-1">
          <MoneyBag className="wh-10" />
          <p className="font-bold text-gray-500">마음 전하실 곳(부의금)</p>
        </div>
        <div
          className="cursor-pointer"
          onClick={() => {
            setFold(!fold);
          }}
        >
          {fold ? <ChevronDown /> : <ChevronUp />}
        </div>
      </div>
      <ResizableBox duration={DURATION}>
        {!fold && (
          <div className="space-y-2 border-gray-300 px-4 pb-3">
            {verifiedAccountList.length > 0
              ? verifiedAccountList.map(renderCashAccount)
              : emptyContent}
            {memberCashAccountContent}
          </div>
        )}
      </ResizableBox>
    </div>
  );
};
